<template>
  <v-container fluid>

    <v-row>
      <v-col>
        <v-card class="ma-5" elevation="8">
          <v-card-title>New region</v-card-title>

          <v-card-text>
            <v-form ref="form" v-model="isValid">
              <v-text-field v-model="regionName" label="Name" prepend-icon="mdi-text" outlined dense required :rules="[globalRules.required]"/>

              <p class="text-body-1">File must be a shapefile in a .zip. The shapefile will get transformed into WebMercator.<br/>The zip file name must be the same as the files names which it contains.</p>
              <v-file-input v-model="file" label="File" accept=".zip" filled dense prepend-icon="mdi-file" show-size :rules="globalRules.fileInput"/>
              <v-row>
                <v-col sm="3">
                  <v-checkbox class="mt-0" v-model="personalizedGrid" label="Personalized Grid"/> <!-- Modulo 1 y 4 -->
                </v-col>
                <v-col sm="2">
                  <v-text-field :disabled="!personalizedGrid" class="right-input" v-model="gridSize" label="Grid Size" outlined dense suffix="m" type="number"/>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>

          <v-card-actions>
            <v-spacer/>
            <!-- <v-progress-circular color="primary" class="mx-4" v-if="isLoadingCreate" :value="uploadProgress" :indeterminate="isProcessingGDAL" :size="35"/> -->
            <v-btn width="150" color="red" class="white--text" @click="clearForm">CLEAR</v-btn>
            <v-btn width="150" color="primary" :disabled="!isValid" @click="initCreateRegion">CREATE</v-btn>
          </v-card-actions>
        </v-card>
      </v-col>

      <v-col>
        <v-card class="ma-5" elevation="8">
          <v-card-title>
            Regions
            <v-spacer/>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-bind="attrs" v-on="on" @click="initRefreshData" :loading="isRefreshingData">
                  <v-icon>mdi-reload</v-icon>
                </v-btn>
              </template>
              <span>Reload</span>
            </v-tooltip>
          </v-card-title>

          <v-card-text>
            <v-data-table :items="regions" :headers="headers" hide-default-footer disable-pagination>
              <!-- Empty table -->
              <template v-slot:no-data>
                <v-alert class="ma-3" color="primary" dark icon="mdi-alert-circle">There are no regions</v-alert>
              </template>

              <!-- Actions row -->
              <template v-slot:[`item.CONTROLS`]="{ item }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on" @click="openFileMap(item)">
                      <v-icon>mdi-eye</v-icon>
                    </v-btn>
                  </template>
                  <span>View on map</span>
                </v-tooltip>

                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on" @click="initDeleteRegion(item)">
                      <v-icon color="red">mdi-delete</v-icon>
                    </v-btn>
                  </template>
                  <span>Delete</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-overlay absolute :value="isInitProcessing">
      <v-progress-circular color="primary" indeterminate size="100" width="10"/>
    </v-overlay>
  </v-container>
</template>

<script>
import { v4 as uuidv4 } from 'uuid'
import { mapGetters, mapActions } from 'vuex'
import api from '../api'

export default {
  name: 'Region',

  data: () => ({
    headers: [
      { text: 'Region', value: 'REGION' },
      { text: 'Shapefile', value: 'NOMBRE_SHAPEFILE' },
      { text: '# of files', value: 'NUM_FICHEROS', align: 'center' },
      { text: 'Actions', value: 'CONTROLS', align: 'center', sortable: false }
    ],
    isValid: false,

    idRegion: null,
    regionName: null,
    file: null,
    // isLoadingCreate: false,
    uploadProgress: 0,

    isRefreshingData: false,

    isInitProcessing: false,
    isProcessing: false,
    intervalCall: null,
    personalizedGrid: false,
    gridSize: 50000
  }),

  computed: {
    ...mapGetters('auth', [
      'idUsuario'
    ]),

    ...mapGetters('region', [
      'regions'
    ]),

    ...mapGetters('file', [
      'fileTypes'
    ])

    /* isProcessingGDAL () {
      return this.isLoadingCreate && (this.uploadProgress === 100)
    } */
  },

  watch: {
    regionName () {
      if (this.regionName) {
        this.regionName = this.regionName.toUpperCase()
      }
    },

    isProcessing (val) {
      this.isInitProcessing = val

      if (val && !this.intervalCall) {
        this.intervalCall = setInterval(() => {
          api.region.getRegionStatus(this.idRegion).then(response => {
            if (response.data.error) {
              this.stopProcessing()

              console.error(response.data.error)
              this.$toastr.e(response.data.message)
            } else {
              if (response.data.finished) {
                this.stopProcessing()

                this.$toastr.s('Region created succesfully')

                this.getRegions()
                this.getFiles()

                this.clearForm()
              } else {
                // Not finished
              }
            }
          }).catch(error => {
            this.stopProcessing()

            console.error(error)
            this.$toastr.e('Error processing region')
          }).finally(() => {
            // Nothing
          })
        }, 10 * 1000) // TODO: ajustar tiempo
      } else {
        clearInterval(this.intervalCall)
        this.intervalCall = null
      }
    }
  },

  methods: {
    ...mapActions('region', [
      'getRegions',
      'createRegion',
      'deleteRegion'
    ]),

    ...mapActions('file', [
      'getFiles'
    ]),

    ...mapActions('app', [
      'setIsProcessing'
    ]),

    clearForm () {
      this.$refs.form.reset()
      this.gridSize = 50000
    },

    stopProcessing () {
      this.isProcessing = false
      clearInterval(this.intervalCall)
      this.intervalCall = null

      this.setIsProcessing(false)
    },

    initRefreshData () {
      this.isRefreshingData = true

      this.getRegions().catch(() => {
        this.$toastr.e('Error updating data')
      }).finally(() => {
        this.isRefreshingData = false
      })
    },

    initCreateRegion () {
      // this.isLoadingCreate = true
      this.uploadProgress = 0

      let requestData = this.getNewRegionRequestData()

      this.idRegion = requestData.region.ID_REGION

      this.isInitProcessing = true
      this.createRegion(requestData).then(() => {
        this.$toastr.s('Start creating region...')
        this.isProcessing = true
      }).catch(() => { // Error uploading
        this.$toastr.e('Error creating region')
        this.isInitProcessing = false
      }).finally(() => {
        /* setTimeout(() => {
          this.isLoadingCreate = false
        }, 700) */
      })
    },

    getNewRegionRequestData () {
      let region = {
        ID_REGION: uuidv4(),
        REGION: this.regionName
      }

      let fileType = this.fileTypes.find(x => x.CODIGO === 6) // Region Shapefile (zip)

      let splitNombre = this.file.name.split('.')
      let format = splitNombre.length > 1 ? splitNombre[splitNombre.length - 1] : ''
      let fileSize = Math.round((this.file.size / 1024) * 100) / 100

      let fileItem = {
        ID_FICHERO: uuidv4(),
        ID_TIPO_FICHERO: fileType.ID_TIPO_FICHERO,
        TIPO_FICHERO: fileType,
        ID_REGION: region.ID_REGION,
        REGION: region,
        ID_USUARIO: this.idUsuario,
        NOMBRE: this.file.name,
        FORMATO: format,
        DESCRIPCION: '',
        FECHA: this.$date.getCurrentDateDB(),
        SIZE_KB: fileSize
      }

      return {
        file: this.file,
        fileItem: fileItem,
        region: region,
        gridSize: this.gridSize,
        progressCallback: this.updateProgress
      }
    },

    updateProgress (progress) {
      this.uploadProgress = progress
    },

    async initDeleteRegion (region) {
      this.$root.$confirmDialog.open('Delete region', 'Are you sure you want to delete the region? This will delete all the files associated to it.').then(async result => {
        if (result) {
          try {
            await this.deleteRegion(region)

            this.$toastr.s('Region deleted correctly')
          } catch (err) {
            this.$toastr.e('Error deleting region')
          }
        }
      })
    },

    openFileMap (region) {
      let idShape = region.ID_SHAPEFILE

      if (idShape) {
        this.$router.push({ path: 'map', query: { idFile: idShape } })
      }
    }
  }
}
</script>

<style scoped>
  .right-input >>> input {
        text-align: right
  }
</style>
